// @flow

/* eslint-env browser */

import * as React from 'react';

import clsx from 'clsx';

import { selectAuthenticatedUserInfos } from '@gandi/authentication';
import { Base, DropDownMenu, DropDownMenuItem, Svg } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { supportsSelector } from '../../helpers/css';
import { useOnClickOutside } from '../../hooks';

import type { NavigationItems } from '../../types';

import UserDropDownMenuItem from './UserDropDownMenuItem';

import styles from './NavigationDropDown.scss';

type Props = {
  id: string,
  label: React.Node | string,
  navigationItems: NavigationItems,
  placement?:
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right',
  tagName?: string,
  className?: string,
  buttonClassName?: string,
  'aria-label'?: string,
};

function NavigationDropDown({
  id,
  label,
  navigationItems,
  tagName,
  className,
  buttonClassName,
  placement,
  ...props
}: Props) {
  const [visible, setVisible] = React.useState(false);
  const dropDownRef = React.useRef<HTMLElement | null>(null);
  const userInfo = useAppSelector(selectAuthenticatedUserInfos);

  useOnClickOutside(dropDownRef, () => {
    if (!visible) {
      return;
    }

    setVisible(false);
  });

  const dropDownId = `dropdown-${id}`;
  const menuPlacement = placement || 'top-left';

  const handleClick = () => {
    if (supportsSelector('.dummy:focus-within')) {
      // …rely on :focus-within if we know it is available.
      return;
    }

    // …set a custom class otherwise.
    setVisible(true);
  };

  const renderTarget = () => (
    <Base
      tagName="a"
      href={`#${dropDownId}`}
      flex
      flexAlignItemsCenter
      flexJustifyCenter
      hoverable
      paddingX="xsmall"
      interactive
      color="base"
      weight="regular"
      className={clsx(styles.button, buttonClassName)}
      onClick={handleClick}
    >
      <Base tagName="span" marginRight="xsmall" flexInline flexAlignItemsCenter>
        {label}
      </Base>
      <Svg name="chevronBottom" className={styles.icon} />
    </Base>
  );

  const renderItem = (item: NavigationItems[number]) =>
    item.id === 'userInfo' && userInfo ? (
      <UserDropDownMenuItem key={item.title} item={item} userInfo={userInfo} />
    ) : (
      <DropDownMenuItem
        key={item.title}
        icon={item.icon}
        to={item.to}
        href={item.href}
        tabIndex="0"
        disabled={item.disabled}
        className={styles.dropdownItem}
      >
        {item.title}
      </DropDownMenuItem>
    );

  return (
    <Base
      id={dropDownId}
      tagName={tagName}
      flex
      flexRow
      flexAlignItemsCenter
      className={clsx(styles.root, className, { [styles.visible]: visible })}
      aria-label={props['aria-label']}
      innerRef={dropDownRef}
    >
      {renderTarget()}
      <div
        className={clsx(styles.dropdown, {
          [styles.top]: menuPlacement.includes('top'),
          [styles.bottom]: menuPlacement.includes('bottom'),
          [styles.left]: menuPlacement.includes('left'),
          [styles.right]: menuPlacement.includes('right'),
        })}
      >
        <DropDownMenu>{navigationItems.map(renderItem)}</DropDownMenu>
      </div>
    </Base>
  );
}

export default (React.memo<Props>(
  NavigationDropDown,
): React.ComponentType<Props>);
